import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Download, FileText, Image as ImageIcon, FileIcon, Loader2 } from "lucide-react";

const PRETTY_BYTES = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function FilePreview({ fileId, open, onOpenChange, label = "Document" }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !fileId) return;
    setLoading(true);
    api.get(`/files/${fileId}/meta`)
      .then(({ data }) => setMeta(data))
      .catch(() => setMeta(null))
      .finally(() => setLoading(false));
  }, [fileId, open]);

  const url = fileId ? `${API}/files/${fileId}` : null;
  const mime = meta?.mime || "";
  const isImage = mime.startsWith("image/");
  const isPdf = mime === "application/pdf";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="file-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isImage ? <ImageIcon className="w-4 h-4 text-cyan-700"/> : isPdf ? <FileText className="w-4 h-4 text-cyan-700"/> : <FileIcon className="w-4 h-4 text-cyan-700"/>}
            {label}
            {meta && <span className="text-xs text-slate-500 font-normal ml-2">· {meta.name} · {PRETTY_BYTES(meta.size)}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-slate-50 border border-slate-200 rounded-md min-h-[400px] flex items-center justify-center">
          {loading && <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />}
          {!loading && !meta && (
            <div className="text-sm text-slate-500 p-6 text-center">
              File metadata unavailable. <a className="text-cyan-700 underline" href={url} target="_blank" rel="noreferrer">Open directly</a>
            </div>
          )}
          {!loading && meta && isImage && (
            <img src={url} alt={meta.name} className="max-w-full max-h-[70vh] object-contain" data-testid="preview-image"/>
          )}
          {!loading && meta && isPdf && (
            <iframe src={url} title={meta.name} className="w-full h-[70vh]" data-testid="preview-pdf"/>
          )}
          {!loading && meta && !isImage && !isPdf && (
            <div className="text-center p-8" data-testid="preview-fallback">
              <FileIcon className="w-10 h-10 text-slate-300 mx-auto mb-2"/>
              <div className="text-sm text-slate-700 font-medium">{meta.name}</div>
              <div className="text-xs text-slate-500 mt-1">{mime || "binary file"} · {PRETTY_BYTES(meta.size)}</div>
              <div className="text-xs text-slate-500 mt-3">This file type can't be previewed inline.</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3">
          {url && (
            <>
              <Button variant="outline" onClick={() => window.open(url, "_blank", "noopener")} data-testid="btn-open-newtab">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5"/> Open in new tab
              </Button>
              <a href={url} download={meta?.name || "download"}>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-download">
                  <Download className="w-3.5 h-3.5 mr-1.5"/> Download
                </Button>
              </a>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
